import { useState, useRef, useCallback, useMemo, useEffect, memo } from "react";
import { useLocalizedStrings } from "../Localization";
import { useLocation } from "react-router-dom";
import { Button, Icon, useToast } from "@aidkitorg/component-library";
import { copyToClipboard, useInterval } from "../Util";
import { usePost } from "../API";

const SCALE_FACTOR = 1.25;

export function TurnableImage(props: { Viewer: string, srcs: string[]; alt: string, fullScreenUrl: string, fullScreen?: boolean }) {
  const L = useLocalizedStrings();
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [imageError, isImageError] = useState(false);
  const imageRefs = useRef<Record<string, HTMLImageElement | null>>({});
  const loadedSrcs = useRef<Record<string, boolean>>({});
  const [imageSrcs, setImageSrcs] = useState(props.srcs);
  const [hiddenImageSrcs, setHiddenImageSrcs] = useState<string[]>([]);
  const toast = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const modernUI = window.location.pathname.includes("/a/");

  const createHandler = useCallback((handler: () => void) => (e: React.MouseEvent) => {
    handler();
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  const rotateRight = useMemo(() => createHandler(() =>
    setRotation((rotation) => (rotation + 90) % 360),
  ), []);

  const rotateLeft = useMemo(() => createHandler(() =>
    setRotation((rotation) => (rotation - 90) % 360),
  ), []);

  const zoomIn = useMemo(() => createHandler(() =>
    setScale((scale) => scale * SCALE_FACTOR),
  ), []);

  const zoomOut = useMemo(() => createHandler(() =>
    setScale((scale) => scale / SCALE_FACTOR),
  ), []);

  const copyLink = useMemo(() => createHandler(() => {
    copyToClipboard(props.fullScreenUrl);
    toast.toast({ variant: 'success', title: L.questions.attachment.copied_link });
  }), []);

  // We need a way to click and pan an image around when zoomed in using a mouse, not just trackpad
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only enable dragging when zoomed in
    if (!scrollContainerRef.current || scale <= 1) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX + scrollContainerRef.current.scrollLeft,
      y: e.clientY + scrollContainerRef.current.scrollTop,
    });
    e.preventDefault();
  }, [scale]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    scrollContainerRef.current.scrollLeft = dragStart.x - e.clientX;
    scrollContainerRef.current.scrollTop = dragStart.y - e.clientY;
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const Image = memo(function Image({ className, src }: { className?: string, src: string }) {
    const setRef = useCallback((element: HTMLImageElement | null) => {
      imageRefs.current[src] = element;
    }, []);

    const handleLoaded = useCallback(() => {
      loadedSrcs.current[src] = true;
    }, []);

    const handleError = useCallback(() => {
      isImageError(true);
      loadedSrcs.current[src] = true;
    }, []);

    return (
      <img
        className={className}
        ref={setRef}
        alt={props.alt}
        style={{
          maxWidth: (props.fullScreen || modernUI) ? "100%" : props.Viewer === 'screener' ? "400px" : "300px",
          transform: 'translateY(' + ((rotation === 90 || rotation === 270) ? (((imageRefs.current[src]?.width || 0) - (imageRefs.current[src]?.height || 0)) / 2) : 0) + 'px) ' +
                     'translateX(' + ((rotation === 90 || rotation === 270) ? (((imageRefs.current[src]?.height || 0) - (imageRefs.current[src]?.width || 0)) / 2) : 0) + 'px) ' +
                     'rotate(' + rotation + 'deg)',
          ...(props.fullScreen ? {objectFit: 'contain', maxHeight: '100%', height: '100%', width: '100%'} : {})
        }}
        onLoad={handleLoaded}
        onError={handleError}
        src={src}
      />
    );
  });

  // The image src will update when the signed url refreshes (every minute or so).
  // In order to avoid having the screen flicker as we render the new src, we first render the new src as a
  // hidden image and then swap it to visible once it has finished rendering.
  const loadNewImageSrcs = useCallback((srcsToLoad: string[]) => {
    setHiddenImageSrcs(srcsToLoad);
    const timer = setInterval(() => {
      if (srcsToLoad.every((src) => loadedSrcs.current[src])) {
        setImageSrcs(srcsToLoad);
        setHiddenImageSrcs([]);
        clearInterval(timer);
      }
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (props.srcs !== imageSrcs) {
      return loadNewImageSrcs(props.srcs);
    }
  }, [props.srcs, loadNewImageSrcs]);

  if (imageError) {
    return (
      <ul>
        {props.srcs.map((src) => (
          <li key={src}>
            <a href={src}
              onClick={async (e) => {
                e.preventDefault();
                window.open(src, "_blank");
              }}
              rel="noopener noreferrer" target="_blank">
              View Document
            </a>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={"py-2 flex flex-col" + (props.fullScreen ? " h-screen" : "")}>
      {props.Viewer === 'screener' &&
        <div className="flex justify-between mb-2 bg-white/95 backdrop-blur-sm p-2 rounded-md shadow-sm border border-gray-200 flex-shrink-0">
          <Button
            className="border-0"
            variant="secondary"
            onClick={rotateRight}
            icon={<Icon name="ArrowUturnRightIcon" />}
          >
            <span className="sr-only">{L.questions.attachment.rotate_image_right}</span>
          </Button>
          <Button
            className="border-0"
            variant="secondary"
            onClick={rotateLeft}
            icon={<Icon name="ArrowUturnLeftIcon" />}
          >
            <span className="sr-only">{L.questions.attachment.rotate_image_left}</span>
          </Button>
          <Button
            className="border-0"
            variant="secondary"
            onClick={zoomIn}
            icon={<Icon name="MagnifyingGlassPlusIcon" />}
          >
            <span className="sr-only">{L.questions.attachment.zoom_in}</span>
          </Button>
          <Button
            className="border-0"
            variant="secondary"
            onClick={zoomOut}
            icon={<Icon name="MagnifyingGlassMinusIcon" />}
          >
            <span className="sr-only">{L.questions.attachment.zoom_out}</span>
          </Button>
          <Button
            className="border-0"
            variant="secondary"
            onClick={copyLink}
            icon={<Icon name="LinkIcon" />}
          >
            <span className="sr-only">{L.questions.attachment.copy_link}</span>
          </Button>
        </div>
      }
      <div
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        className="overflow-auto flex-1"
        style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        <div style={{
          // We want to scale out from the top so that the top of the image doesn't move,
          // but we want to rotate the image from the center. Thus we scale the container of the image,
          // not the image itself, so it can have a different transform origin.
          transformOrigin: 'top left',
          transform: 'scale(' + scale + ')',
        }}>
          {imageSrcs?.map((src) => <Image key={src} src={src} />)}
          {hiddenImageSrcs?.map((src) => <Image key={src} className="hidden" src={src} />)}
        </div>
      </div>
    </div>
  );
}

export function FullScreenTurnableImagePage() {
  const location = useLocation();
  const getSignedURL = usePost('/v2/document/view');
  const [signedURLs, setSignedURLs] = useState<string[]>([]);

  const fetchSignedURLs = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const src = params.get("src");
    if (!src) return;

    try {
      const paths = await getSignedURL({
        paths: [src],
      });
      setSignedURLs(paths.paths[src] ?? []);
    } catch (error) {
      console.error('Failed to fetch signed URL:', error);
    }
  }, [location]);
  // The signed urls expire in 60 seconds, so refresh the url ahead of that expiry
  useInterval(fetchSignedURLs, 50_000, { fireImmediately: true, runInBackground: true });

  if (signedURLs.length === 0) {
    return <p>Error: No image source provided.</p>;
  }
  return (
    <div className="w-screen h-screen">
      <TurnableImage Viewer={'screener'} srcs={signedURLs} alt="Full screen turnable image" fullScreen={true} fullScreenUrl={window.location.href} />
    </div>
  );
}
